import {clsx} from 'clsx'
import {useQuery} from 'react-query'
import {useRouter} from 'next/router'
import type {Elements, IContentItem} from '@kontent-ai/delivery-sdk'
import type {Block, Tersed} from '@/services/kontent-ai/types'
import {SkeletonLoader} from '@/products/flexible-web-toolkit/components/skeleton-loader'
import {spacing} from '@/products/flexible-web-toolkit/styles'
import type {CouponContentItem} from '@/products/your-pet-and-you/blocks/coupon'
import type {ListerItemsQueryKey} from '@/products/flexible-web-toolkit/blocks/module-coupon-lister/types'
import {fetchListerItems} from '@/services/lister-items/client'

export type ModuleCouponListerContentItem = IContentItem<{
	snippetSpacingSpacing: Elements.MultipleChoiceElement
	paginationString: Elements.TextElement
}>

export const ModuleCouponListerBlock: Block<ModuleCouponListerContentItem> = ({
	BlockMapper,
	block,
	globalConfig,
	page,
}) => {
	const serverRenderedBlock = block as typeof block & {
		initialItems?: Tersed<CouponContentItem>[]
	}
	const {locale = ''} = useRouter()
	const {data, isFetching, isLoading} = useQuery({
		queryKey: [
			'coupons',
			{codename: 'coupon', locale},
		] as ListerItemsQueryKey,
		queryFn: (opts) => fetchListerItems<Tersed<CouponContentItem>>(opts),
	})

	const componentSpacing =
		spacing[
			(block.elements.snippetSpacingSpacing[0]?.codename ??
				'none') as keyof typeof spacing
		]

	const pagination = block.elements.paginationString.replace(
		/{{X}}/,
		(
			data?.items ??
			serverRenderedBlock.initialItems ??
			[]
		).length.toString()
	)
	const couponItems = data?.items ?? serverRenderedBlock.initialItems ?? []
	const shouldShowInitialSkeleton = !data && couponItems.length === 0
	const shouldShowCoupons =
		((!isLoading && data) || couponItems.length > 0) &&
		couponItems.length > 0

	return (
		<div className={clsx(`mx-auto max-w-screen-xl`, componentSpacing)}>
			<div className="flex flex-col items-center">
				<h3>{pagination}</h3>
				{shouldShowInitialSkeleton || (isFetching && data) ? (
					<div className="mt-20 w-full max-w-[764px] space-y-20 px-3 sm:w-3/4 sm:px-0">
						<SkeletonLoader amount={7} />
					</div>
				) : null}
				{shouldShowCoupons
					? couponItems.map((coupon) => {
							return (
								<div
									className="my-2 flex w-full flex-wrap px-3 sm:w-3/4 sm:px-0"
									key={coupon.system.id}
								>
									<BlockMapper
										blocks={[coupon]}
										globalConfig={globalConfig}
										page={page}
									/>
								</div>
							)
						})
					: null}
			</div>
		</div>
	)
}
